// Key Storage with Web Cryptography API
//
// Copyright 2014 Info Tech, Inc.
// Provided under the MIT license.
// See LICENSE file for details.

// Saves cryptographic key pairs in IndexedDB.

import IndexDBStorage from '@axisnow/storage/IndexedDBStorage';
import Promise from 'bluebird';

var _storage;
var self;

var _tempSavedKey;
var isIE = !!window.msCrypto;

var KeyStore = function() {
  _storage = new IndexDBStorage();
};
// saveKey method
//
// Takes the public and private keys, and an arbitrary name
// for the saved key. The private key can be passed as null if unavailable.
//
// Returns a Promise. If a key can be saved, the
// Promise is fulfilled with a copy of the object
// that was saved. Otherwise, it is rejected with an Error.
//
KeyStore.prototype.saveKey = function(publicKey, privateKey, name) {
  var self = this;

  return window.crypto.subtle.exportKey('spki', publicKey).then(function(spki) {
    var keyObject = {
      publicKey: publicKey,
      privateKey: privateKey,
      name: name,
      spki: spki,
    };

    return _storage.isIndexDBSupported().then(function(result) {
      if (!result) {
        console.log(keyObject);
        _tempSavedKey = keyObject;
        return keyObject;
      } else {
        if (isIE || _storage.isWebkit()) {
          keyObject._publicKey = publicKey._key;
          keyObject._privateKey = privateKey._key;
        }

        return _storage.add(_storage.STORE_NAMES.KeyStore, keyObject);
      }
    });
  });
};

// getKey method
//
// Takes the name of a property (one of id, name, or spki), and
// the value of that property to search for.
//
// Returns a Promise. If a key with the given propertyValue of
// the specified propertyName exists in the database, the Promise
// is fulfilled with the saved object, otherwise it is rejected
// with an Error.
//
// If there are multiple objects with the requested propertyValue,
// only one of them is passed to the fulfill function.
//
KeyStore.prototype.getKey = function(propertyName, propertyValue) {
  return _storage.isIndexDBSupported().then(function(result) {
    if (!result) {
      return Promise.resolve(_tempSavedKey);
    } else {
      return _storage
        .get(_storage.STORE_NAMES.KeyStore, propertyName, propertyValue)
        .then(function(savedObject) {
          if (!isIE && !_storage.isWebkit()) return savedObject;

          if (savedObject != null) {
            savedObject.publicKey = new CryptoKey(
              savedObject._publicKey,
              savedObject.publicKey.algorithm,
              savedObject.publicKey.extractable,
              savedObject.publicKey.usages,
            );
            savedObject.privateKey = new CryptoKey(
              savedObject._privateKey,
              savedObject.privateKey.algorithm,
              savedObject.privateKey.extractable,
              savedObject.privateKey.usages,
            );
            delete savedObject._publicKey;
            delete savedObject._privateKey;
          }
          return savedObject;
        });
    }
  });
};

// close method
//
// Takes no parameters.
//
// Simply closes the database and returns immediately. Note that
// the IndexedDB system actually closes the database in a separate
// thread, and there is no way to know when that process is complete.
//
KeyStore.prototype.close = function() {
  return _storage.close();
};

export default KeyStore;
